import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ChevronDown, Check } from 'lucide-react';
import { Skeleton } from './primitives';
import { useCourseList } from '../hooks/useCourseList';

/**
 * Custom course filter dropdown (NOT a native <select>).
 *
 * Renders a styled trigger button + a popover menu with a scrollable list of
 * courses, backed by the lightweight GET /api/course/names/{userId} endpoint.
 * Built entirely from theme tokens so it looks polished in light AND dark mode.
 *
 * Props:
 *   value        – string  – currently selected courseId, or "" for "all"
 *   onChange     – (courseId: string, courseName: string) => void
 *   placeholder  – string  – label for the "all" option (default: "All courses")
 */

const FILTER_WIDTH = '220px';

const Anchor = styled.div`
  position: relative;
  flex: none;
  width: ${FILTER_WIDTH};
  max-width: min(${FILTER_WIDTH}, 32vw);
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: ${({ theme }) => theme.controlH};
  padding: 0 12px;
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.fsSm};
  font-family: ${({ theme }) => theme.fontBody};
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  box-sizing: border-box;

  &:hover,
  &.is-open {
    border-color: ${({ theme }) => theme.borderStrong};
  }
  &:focus-visible {
    border-color: ${({ theme }) => theme.accent};
  }

  .cfd__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
  .cfd__chev {
    flex: none;
    width: 15px;
    height: 15px;
    color: ${({ theme }) => theme.textMuted};
    transition: transform 0.15s;
  }
  &.is-open .cfd__chev {
    transform: rotate(180deg);
  }
`;

const Menu = styled.div`
  position: fixed;
  box-sizing: border-box;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.shadowLg};
  padding: 4px;
  z-index: 200;
  overflow: hidden;
`;

const List = styled.div`
  max-height: min(320px, calc(100vh - 120px));
  min-height: 0;
  overflow-x: hidden;
  overflow-y: scroll;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.borderStrong} transparent`};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.borderStrong};
    border-radius: 999px;
  }
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
  margin-bottom: 2px;
  padding: 8px 8px;
  border: none;
  border-radius: ${({ theme }) => theme.rSm};
  background: ${({ theme, $selected }) => ($selected ? theme.accentSoft : 'transparent')};
  color: ${({ theme, $selected }) => ($selected ? theme.accentStrong : theme.text)};
  font-size: ${({ theme }) => theme.fsSm};
  font-family: ${({ theme }) => theme.fontBody};
  font-weight: ${({ $selected }) => ($selected ? 650 : 500)};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  box-sizing: border-box;

  &:hover {
    background: ${({ theme, $selected }) => ($selected ? theme.accentSoft : theme.surface3)};
  }

  .cfd__rowlabel {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.35;
  }
  .cfd__check-slot {
    flex: none;
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.accent};
  }
`;

const CourseFilterDropdown = ({
  value = '',
  onChange,
  placeholder = 'All courses',
}) => {
  const { courses, coursesLoading } = useCourseList();
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);

  const updateMenuRect = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuRect({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Close the popover on outside click / Escape — mirrors the TopBar
  // notifications popover pattern so behaviour is consistent across the bar.
  useEffect(() => {
    if (!open) return undefined;
    updateMenuRect();
    const onDown = (e) => {
      const target = e.target;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', updateMenuRect);
    window.addEventListener('scroll', updateMenuRect, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updateMenuRect);
      window.removeEventListener('scroll', updateMenuRect, true);
    };
  }, [open, updateMenuRect]);

  // Wheel on the list itself — capture-phase stopPropagation on the menu ancestor
  // blocked events from reaching this scrollport, so scroll never moved.
  useEffect(() => {
    if (!open) return undefined;
    const listEl = listRef.current;
    if (!listEl) return undefined;

    const onWheel = (e) => {
      if (listEl.scrollHeight <= listEl.clientHeight) return;

      const maxScroll = listEl.scrollHeight - listEl.clientHeight;
      const nextScrollTop = Math.max(0, Math.min(listEl.scrollTop + e.deltaY, maxScroll));

      if (nextScrollTop !== listEl.scrollTop) {
        listEl.scrollTop = nextScrollTop;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    listEl.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      listEl.removeEventListener('wheel', onWheel);
    };
  }, [open, menuRect]);

  const selectCourse = useCallback((courseId, courseName) => {
    if (onChange) onChange(courseId, courseName);
    setOpen(false);
  }, [onChange]);

  // While the (cached) course list is loading, shimmer the trigger at a fixed
  // width so the toolbar layout doesn't shift once the names resolve.
  if (coursesLoading) {
    return <Skeleton $w={FILTER_WIDTH} $h="34px" $r="12px" />;
  }

  const selected = courses.find((c) => String(c.id) === String(value));
  const triggerLabel = value && selected ? selected.name : placeholder;

  return (
    <Anchor ref={anchorRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        className={open ? 'is-open' : ''}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by course"
        title={triggerLabel}
      >
        <span className="cfd__label">{triggerLabel}</span>
        <ChevronDown size={15} className="cfd__chev" />
      </Trigger>

      {open && menuRect && createPortal(
        <Menu
          ref={menuRef}
          role="listbox"
          style={{
            top: menuRect.top,
            left: menuRect.left,
            width: menuRect.width,
          }}
        >
          <List ref={listRef} className="cfd__list">
            <Row
              type="button"
              role="option"
              aria-selected={!value}
              $selected={!value}
              onClick={() => selectCourse('', '')}
            >
              <span className="cfd__rowlabel">{placeholder}</span>
              <span className="cfd__check-slot" aria-hidden="true">
                {!value && <Check size={15} />}
              </span>
            </Row>

            {courses.map((c) => {
              const isSel = String(c.id) === String(value);
              return (
                <Row
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  $selected={isSel}
                  onClick={() => selectCourse(String(c.id), c.name)}
                  title={c.name}
                >
                  <span className="cfd__rowlabel">{c.name}</span>
                  <span className="cfd__check-slot" aria-hidden="true">
                    {isSel && <Check size={15} />}
                  </span>
                </Row>
              );
            })}
          </List>
        </Menu>,
        document.body,
      )}
    </Anchor>
  );
};

export default CourseFilterDropdown;
